import React, {useContext, useState, useEffect} from 'react';
import {FormattedMessage, injectIntl} from "react-intl";
import PropTypes from 'prop-types';

import {Formik, Form as FormikForm} from "formik";

import Grid from "@mui/material/Grid";

import FormBloc from "components/form/FormBloc";
import ActionsContainer from "components/containers/ActionsContainer";
import CancelButtonStyled from "components/button/CancelButtonStyled";
import SaveButton from "components/button/SaveButton";
import {ListContext} from "components/List/context/ListContext";

import NameField from "./components/NameField";
import SyliusCodeField from "./components/SyliusCodeField";
import MarketplaceCodeField from "./components/MarkeplaceCodeField";
import MethodTypeField from "./components/MethodTypeField";
import SkipReservedStepField from "./components/SkipReservedStepField";
import WaybillFields from "./components/WaybillFields";
import GoogleIconField from "./components/GoogleIconField";
import {ShippingMethodFormContext} from "./context/ShippingMethodFormContext";
import ReturnWaybillFields from "./components/ReturnWaybillFields";
import AdditionalDataFields from "./components/AdditionalDataFields";

import update from "api/shippingMethod/update";
import {create} from "api/shippingMethod/create";

import getValidationSchema from "./validationSchema";
import CarrierIconField from "./components/CarrierIconField";

ShippingMethodForm.propTypes = {
    refreshList: PropTypes.func.isRequired
};
function ShippingMethodForm({refreshList, ...props}) {
    const [isReturnWaybill, setIsReturnWaybill] = useState(false);
    const [isApi, setIsApi] = useState(false);
    const [isWaybill, setIsWaybill] = useState(false);

    const {
        loading,
        selectedItem,
        notFilteredListItems,
        closeCreateDialog,
    } = useContext(ListContext)

    const initialValues = {
        'name': selectedItem?.name || '',
        'shopName': selectedItem?.shopName || '',
        'icon': selectedItem?.icon || null,
        'carrierIcon': selectedItem?.carrierIcon || null,
        'shippingMethodShopName': selectedItem?.shippingMethodShopName || '',
        'discr': selectedItem?.discr || '',
        'skipReservedStep': selectedItem?.skipReservedStep || false,
        'senderName': selectedItem?.senderName || null,
        'contactMailAddress': selectedItem?.contactMailAddress || null,
        'contactPhoneNumber': selectedItem?.contactPhoneNumber || null,
        'senderAddress': selectedItem?.senderAddress || null,
        'contractNumber': selectedItem?.contractNumber || null,
        'wsPassword': selectedItem?.wsPassword || null,
        'printConfig': selectedItem?.printConfig || {
            'returnShippingMethodOverride': null,
            'printReturnWaybill': false
        },
        'additionalData': {
            sandbox: selectedItem?.additionalData?.sandbox || false,
            customerNumber: selectedItem?.additionalData?.customerNumber || null, // nshift
            carrierServiceId: selectedItem?.additionalData?.carrierServiceId || null, // nshift
            returnCarrierServiceId: selectedItem?.additionalData?.returnCarrierServiceId || null, // nshift
            api_url: selectedItem?.additionalData?.api_url || null, // this field and below are for geodis
            api_username: selectedItem?.additionalData?.api_username || null,
            api_key: selectedItem?.additionalData?.api_key || null,
            agency_code: selectedItem?.additionalData?.agency_code || null,
            client_code: selectedItem?.additionalData?.client_code || null,
            product_code: selectedItem?.additionalData?.product_code || null,
            sender_name: selectedItem?.additionalData?.sender_name || null,
            sender_country_code: selectedItem?.additionalData?.sender_country_code || null,
            sender_post_code : selectedItem?.additionalData?.sender_post_code || null,
            sender_city: selectedItem?.additionalData?.sender_city || null,
        },
    };

    // when editing, check if the fields are filled in to show them in the form
    useEffect(() => {
        if (selectedItem === null) {
            return;
        }

        // show waybill fields
        ['senderName', 'contactMailAddress', 'contactPhoneNumber', 'senderAddress'].some((fieldName) => {
            if (selectedItem && selectedItem[fieldName] && selectedItem[fieldName] !== null) {
                setIsWaybill(true);
                return true;
            }
        });

        // show api fields
        ['contractNumber', 'wsPassword'].some((fieldName) => {
            if (selectedItem && selectedItem[fieldName] && selectedItem[fieldName] !== null) {
                setIsApi(true);
                return true;
            }
        })

        // show return waybill fields
        if (selectedItem?.printConfig?.returnShippingMethodOverride && selectedItem.printConfig.returnShippingMethodOverride !== '') {
            setIsReturnWaybill(true);
        }
    }, [selectedItem]);

    function setConditionalValues(values) {
        if (!isWaybill) {
            values.senderName = null;
            values.senderAddress = null;
            values.contactMailAddress = null;
            values.contactPhoneNumber = null;
            values.contractNumber = null;
            values.wsPassword = null;
        }

        if (!isApi) {
            values.contractNumber = null;
            values.wsPassword = null;
        }

        if (!isReturnWaybill) {
            delete values.printConfig.returnShippingMethodOverride;
            delete values.printConfig.printReturnWaybill;
        }

        return values;
    }

    return (
        <ShippingMethodFormContext.Provider value={{
            isApi,
            setIsApi,
            isWaybill,
            setIsWaybill,
            isReturnWaybill,
            setIsReturnWaybill,
        }}>
            <Formik
                initialValues={initialValues}
                validationSchema={getValidationSchema(isWaybill, isReturnWaybill, notFilteredListItems, !!selectedItem)}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
                validateOnMount={true}
                onSubmit={(values, {setSubmitting}) => {
                    let promise;

                    values = setConditionalValues(values);

                    if (selectedItem) {
                        promise = update(selectedItem, values);
                    } else {
                        promise = create(values);
                    }

                    promise.then(() => {
                        closeCreateDialog();
                        refreshList()
                    }).finally(() => setSubmitting(false));
                }}
            >
                {({
                      isSubmitting,
                      isValid,
                }) => (
                    <FormikForm>
                        <Grid container rowSpacing={6}>
                            <FormBloc
                                titleId={selectedItem ? 'shippingMethods.form.title.edit' : 'shippingMethods.form.title.create'}
                                subtitleId='shippingMethods.form.subtitle'
                            >
                                <Grid container rowSpacing={3}>
                                    <Grid item xs={12}>
                                        <NameField />
                                    </Grid>
                                    <Grid item container columnSpacing={2}>
                                        <Grid item xs={6}>
                                            <SyliusCodeField />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MarketplaceCodeField />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GoogleIconField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CarrierIconField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SkipReservedStepField />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MethodTypeField />
                                    </Grid>
                                    <WaybillFields />
                                    <Grid item container>
                                        <AdditionalDataFields />
                                    </Grid>
                                    <ReturnWaybillFields />
                                </Grid>
                            </FormBloc>
                            <Grid item xs={12}>
                                <ActionsContainer>
                                    <CancelButtonStyled
                                        disabled={isSubmitting}
                                        onClick={closeCreateDialog}
                                    >
                                        <FormattedMessage id="list.button.cancel" />
                                    </CancelButtonStyled>
                                    <SaveButton
                                        type={"submit"}
                                        disabled={loading || !isValid || isSubmitting}
                                    >
                                        {selectedItem ? (
                                            <FormattedMessage id="shippingMethods.form.button.edit" />
                                        ) : (
                                            <FormattedMessage id="shippingMethods.form.button.create" />
                                        )}
                                    </SaveButton>
                                </ActionsContainer>
                            </Grid>
                        </Grid>
                    </FormikForm>
                )}
            </Formik>
        </ShippingMethodFormContext.Provider>
    );
}

export default injectIntl(ShippingMethodForm);
